import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, newKey } from "@/lib/api";
import { Field } from "@/components/admin/AdminUI";
import { VariantPicker } from "@/components/admin/VariantPicker";

export const ADJ_TYPES = [
  ["increase", "Increase on-hand"],
  ["decrease", "Decrease on-hand"],
  ["mark_damaged", "Mark damaged (on-hand stays, becomes unsellable)"],
  ["restore_damaged", "Restore damaged to sellable"],
  ["write_off_damaged", "Write off damaged stock"],
];
export const REASONS = [
  ["count_correction", "Count correction"], ["found", "Found"], ["lost", "Lost"], ["theft", "Theft"], ["damaged_in_storage", "Damaged in storage"], ["damaged_in_transit", "Damaged in transit"],
  ["quality_issue", "Quality issue"], ["repaired", "Repaired"], ["write_off", "Write-off"], ["sample", "Sample / press loan"], ["opening_balance", "Opening balance"], ["other", "Other (describe)"],
];

/** Post an idempotent stock adjustment. `preset` may contain {warehouse_id, variant}. */
export const StockAdjustDialog = ({ open, onOpenChange, warehouses, preset, onDone }) => {
  const [form, setForm] = useState({ warehouse_id: "", variant: null, type: "increase", quantity: "1", reason_code: "count_correction", note: "" });
  const [key, setKey] = useState(newKey());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ warehouse_id: preset?.warehouse_id || warehouses.find((w) => w.is_default)?.id || warehouses[0]?.id || "", variant: preset?.variant || null, type: preset?.type || "increase", quantity: "1", reason_code: "count_correction", note: "" });
      setKey(newKey());
    }
  }, [open, preset, warehouses]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.variant) return toast.error("Select a variant");
    setBusy(true);
    try {
      const r = await api.post("/admin/inventory/adjustments", { warehouse_id: form.warehouse_id, variant_id: form.variant.id, type: form.type, quantity: Number(form.quantity), reason_code: form.reason_code, note: form.note, idempotency_key: key });
      toast.success(r.data.replay ? "Adjustment already applied (idempotent replay)" : `Stock updated · ${form.variant.sku} now ${r.data.balance?.available ?? "—"} available`);
      onOpenChange(false);
      onDone?.(r.data);
    } catch (err) {
      toast.error(errMsg(err, "Adjustment rejected"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="stock-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-medium">Adjust stock</DialogTitle>
          <DialogDescription>Creates an immutable movement in the ledger. Negative results are rejected; the request is idempotent.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <Field label="Variant" required><VariantPicker value={form.variant} onSelect={(v) => setForm({ ...form, variant: v })} testId="adjust-variant-picker" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Warehouse" htmlFor="adj-wh" required>
              <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger id="adj-wh" data-testid="adjust-warehouse-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.filter((w) => w.status === "active").map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Quantity" htmlFor="adj-qty" required><Input id="adj-qty" type="number" min={1} max={100000} required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="adjust-quantity-input" /></Field>
            <Field label="Adjustment type" htmlFor="adj-type" required className="sm:col-span-2">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger id="adj-type" data-testid="adjust-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ADJ_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Reason" htmlFor="adj-reason" required className="sm:col-span-2">
              <Select value={form.reason_code} onValueChange={(v) => setForm({ ...form, reason_code: v })}>
                <SelectTrigger id="adj-reason" data-testid="adjust-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Note" htmlFor="adj-note" className="sm:col-span-2" hint={form.reason_code === "other" ? "Required when the reason is 'other'" : "Optional context for the audit trail"}><Textarea id="adj-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="adjust-note-input" /></Field>
          </div>
          <p className="text-[11px] text-muted-foreground font-mono">Idempotency key {key}</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || !form.variant || !form.warehouse_id} data-testid="adjust-submit-button">{busy ? "Posting…" : "Post adjustment"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
